export const DEFAULT_CURRENCY = 'LKR';
export const DEFAULT_LOCALE = 'en-LK';

const parseAmount = (amount: string | number): number => {
  const n = typeof amount === 'string' ? parseFloat(amount) : amount;
  return Number.isFinite(n) ? n : 0;
};

export const getCurrencyCode = (): string => {
  const stored = localStorage.getItem('company_currency');
  if (stored) return stored;

  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.company?.currency) return user.company.currency;
  } catch {
    // ignore invalid stored user
  }

  return DEFAULT_CURRENCY;
};

export const formatCurrency = (
  amount: string | number,
  options?: { minimumFractionDigits?: number; maximumFractionDigits?: number }
): string => {
  const maximumFractionDigits = options?.maximumFractionDigits ?? 2;
  const minimumFractionDigits = Math.min(
    options?.minimumFractionDigits ?? 2,
    maximumFractionDigits
  );

  return new Intl.NumberFormat(DEFAULT_LOCALE, {
    style: 'currency',
    currency: getCurrencyCode(),
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(parseAmount(amount));
};

export const formatQuantity = (
  amount: string | number,
  options?: { minimumFractionDigits?: number; maximumFractionDigits?: number }
): string => {
  const maximumFractionDigits = options?.maximumFractionDigits ?? 4;
  const minimumFractionDigits = Math.min(
    options?.minimumFractionDigits ?? 2,
    maximumFractionDigits
  );

  return new Intl.NumberFormat(DEFAULT_LOCALE, {
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(parseAmount(amount));
};

/** Preview/view qty: whole numbers without decimals; fractional values at 2dp. */
export const formatPreviewQuantity = (amount: string | number): string => {
  const n = parseAmount(amount);
  if (!Number.isFinite(n)) return '';
  const normalized = Math.round(n * 10_000) / 10_000;
  const isWhole = Math.abs(normalized - Math.round(normalized)) < 0.000_05;

  return new Intl.NumberFormat(DEFAULT_LOCALE, {
    minimumFractionDigits: isWhole ? 0 : 2,
    maximumFractionDigits: isWhole ? 0 : 2,
  }).format(isWhole ? Math.round(normalized) : normalized);
};

/** Whole numbers for charts and compact KPI display */
export const formatCompactNumber = (amount: string | number): string =>
  new Intl.NumberFormat(DEFAULT_LOCALE, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(parseAmount(amount));
